import {COLORS, SCREEN_LG} from 'shared/consts';
import {adaptiveValue, toEm, toRem} from 'shared/helpers';
import styled, {css} from 'styled-components';
import {Link} from 'react-router-dom';

export const ApiarySchemeWrapper = styled.div`
  margin: 0 ${toRem(-16)};
  height: calc(100dvh - ${toRem(97)});
  position: relative;

  @media (width < ${toEm(SCREEN_LG)}) {
    height: calc(100dvh - ${toRem(97)} - ${toRem(56)});
  }
`;

export const ApiarySchemeControls = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  gap: ${toRem(8)};
  padding: ${toRem(20)};
  pointer-events: none;
  > * {
    pointer-events: all;
  }
`;

export const ApiarySchemeEditButtons = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${toRem(8)};
  align-items: center;
`;

export const ApiarySchemeSizes = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${toRem(1)};
  background-color: ${COLORS.BG.SURFACE1};
  box-shadow:
    0px 12px 20px 0px #4453710d,
    0px 0px 20px 0px #4453711a;
  color: ${COLORS.ACCENT.DEFAULT};
  border-radius: ${toRem(8)};
  overflow: hidden;
`;

export const ApiarySchemeSizeButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  aspect-ratio: 1;
  font-size: ${toRem(24)};
  position: relative;
  ${adaptiveValue('width', 48, 36, 1440, 480)};

  &:not(:last-child) {
    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      translate: 0 -50%;
      margin: 0 auto;
      height: ${toRem(2)};
      background-color: ${COLORS.ACCENT.SOFT};
      width: 50%;
    }
  }
`;

type ApiarySchemeModalWrapperProps = {
  $isOpen?: boolean;
};
export const ApiarySchemeModalWrapper = styled.div<ApiarySchemeModalWrapperProps>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  transition: opacity 0.3s ease 0s;

  ${(props) =>
    !props.$isOpen &&
    css`
      opacity: 0;
      pointer-events: none;
    `}
`;

type ApiarySchemeModalPopupProps = {
  $top: number;
  $left: number;
  $isOpen?: boolean;
};
export const ApiarySchemeModalPopup = styled.div<ApiarySchemeModalPopupProps>`
  border-radius: ${toRem(16)};
  padding: ${toRem(16)};
  background-color: ${COLORS.BG.SURFACE2};
  position: absolute;
  top: ${(props) => toRem(props.$top || 0)};
  left: ${(props) => toRem(props.$left || 0)};
  width: ${toRem(329)};
  box-shadow:
    0px 32px 32px 0px ${COLORS.NEUTRAL.CONTAINER_SOFT},
    0px 0px 32px 0px ${COLORS.NEUTRAL.CONTAINER_MUTED};
  transition:
    opacity 0.3s ease 0s,
    translate 0.3s ease 0s;

  ${(props) => {
    if (!props.$isOpen) {
      return css`
        translate: ${toRem(-50)} 0;
      `;
    }

    return '';
  }}
`;

export const ApiarySchemeWarnToast = styled.div`
  font-size: ${toRem(14)};
  line-height: ${18 / 14};

  a {
    color: ${COLORS.ACCENT.DEFAULT};
    @media (any-hover: hover) {
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

export const ApiarySchemeModalHead = styled.div`
  display: grid;
  grid-template-columns: auto ${toRem(24)};
  gap: ${toRem(4)} ${toRem(16)};
  justify-content: space-between;

  &:not(:last-child) {
    margin-bottom: ${toRem(16)};
  }
`;

export const ApiarySchemeModalTitle = styled.h3`
  font-weight: 700;
  font-size: ${toRem(18)};
  line-height: ${24 / 18};
`;

export const ApiarySchemeShareActions = styled.div`
  grid-column: span 2;
  display: flex;
  gap: ${toRem(8)};
`;

export const ApiarySchemeModalTag = styled.div<{
  variant?: 'default' | 'active';
}>`
  padding: ${toRem(4)} ${toRem(8)};
  width: max-content;
  border-radius: ${toRem(8)};
  font-weight: 500;
  font-size: ${toRem(12)};
  line-height: ${16 / 12};

  background-color: ${({variant}) =>
    variant === 'active' ? COLORS.SUCCESS.CONTAINER_DEFAULT : COLORS.STATUS_01.CONTAINER_DEFAULT};

  color: ${({variant}) =>
    variant === 'active' ? COLORS.SUCCESS.ON_CONTAINER : COLORS.STATUS_01.DEFAULT};
`;

export const ApiarySchemeShareLink = styled.button`
  display: flex;
  align-items: center;
  font-weight: 500;
  font-size: ${toRem(16)};
  line-height: ${24 / 16};
  gap: ${toRem(8)};
  color: ${COLORS.NEUTRAL.DEFAULT};
  span,
  path {
    transition:
      color 0.3s ease 0s,
      fill 0.3s ease 0s;
  }

  @media (any-hover: hover) {
    &:hover {
      color: ${COLORS.ACCENT.HOVER};
      path {
        fill: ${COLORS.ACCENT.HOVER};
      }
    }
  }
`;

export const ApiarySchemeModalItem = styled.div`
  background-color: ${COLORS.BG.ELEVATED.L};
  padding: ${toRem(16)};
  border-radius: ${toRem(16)};

  &:not(:last-child) {
    margin-bottom: ${toRem(8)};
  }

  @media (width < ${toEm(SCREEN_LG)}) {
    background-color: ${COLORS.BG.SURFACE2};
  }
`;

export const ApiarySchemeModalInfoWrapper = styled.div`
  display: grid;
  grid-template-columns: auto ${toRem(123)};
  gap: ${toRem(16)};

  &:not(:last-child) {
    margin-bottom: ${toRem(12)};
  }
`;

export const ApiarySchemeModalInfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${toRem(16)};
  justify-content: space-between;
`;

export const ApiarySchemeModalRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${toRem(2)};

  span {
    color: ${COLORS.FG.SOFT};
    font-size: ${toRem(12)};
    line-height: ${16 / 12};
  }

  p {
    font-weight: 500;
    font-size: ${toRem(14)};
    line-height: ${20 / 14};
  }
`;

export const ApiarySchemeModalImage = styled.div`
  width: ${toRem(123)};
  height: ${toRem(146)};
  position: relative;

  > img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  p {
    position: absolute;
    left: 50%;
    top: ${toRem(48)};
    width: ${toRem(22)};
    height: ${toRem(22)};
    translate: -50% 0;

    img {
      width: 100%;
      aspect-ratio: 1;
    }

    span {
      position: absolute;
      top: 50%;
      left: 50%;
      translate: -50% -50%;
      font-weight: 500;
      font-size: ${toRem(12)};
      line-height: ${16 / 12};
      color: ${COLORS.STATIC.WHITE};
    }
  }
`;

export const ApiarySchemeModalSubtitle = styled.h4`
  font-weight: 700;
  font-size: ${toRem(16)};
  line-height: ${24 / 16};
  &:not(:last-child) {
    margin-bottom: ${toRem(8)};
  }
`;

export const ApiarySchemeModalDeviceTagWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${toRem(12)};

  &:not(:last-child) {
    margin-bottom: ${toRem(12)};
  }
`;

export const ApiarySchemeModalDeviceTag = styled.div`
  display: flex;
  align-items: center;
  gap: ${toRem(4)};
  padding: ${toRem(2)} ${toRem(8)};
  border-radius: ${toRem(8)};
  background-color: ${COLORS.NEUTRAL.CONTAINER_DEFAULT};
  font-weight: 500;
  font-size: ${toRem(12)};
  line-height: ${16 / 12};
  color: ${COLORS.NEUTRAL.DEFAULT};
`;

export const ApiarySchemeModalLink = styled(Link)`
  display: flex;
  align-items: flex-end;
  font-weight: 500;
  font-size: ${toRem(13)};
  line-height: ${23 / 13};
  color: ${COLORS.ACCENT.DEFAULT};
`;

export const ApiarySchemeModalDrawer = styled.div`
  padding: ${toRem(16)};
`;

export const ApiarySchemeInfoButton = styled.button`
  position: absolute;
  bottom: ${toRem(20)};
  left: ${toRem(20)};
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${toRem(48)};
  height: ${toRem(48)};
  background-color: ${COLORS.BG.SURFACE1};
  border-radius: ${toRem(8)};
  box-shadow:
    0px 12px 20px 0px #4453710d,
    0px 0px 20px 0px #4453711a;
  border: none;
  cursor: pointer;
  transition: opacity 0.2s ease;
  z-index: 10;

  &:hover {
    opacity: 0.8;
  }

  svg {
    width: ${toRem(24)};
    height: ${toRem(24)};
  }
`;

export const ApiarySchemeInfoModal = styled.div`
  position: absolute;
  bottom: ${toRem(20)};
  left: ${toRem(20)};
  padding: ${toRem(16)};
  background-color: ${COLORS.BG.SURFACE1};
  border-radius: ${toRem(16)};
  box-shadow:
    0px 12px 20px 0px #4453710d,
    0px 0px 20px 0px #4453711a;
  min-width: ${toRem(280)};
  z-index: 10;
`;

export const ApiarySchemeInfoTitle = styled.h3`
  margin: 0 0 ${toRem(16)} 0;
  font-weight: 700;
  font-size: ${toRem(16)};
  line-height: ${24 / 16};
  color: ${COLORS.FG.DEFAULT};
`;

export const ApiarySchemeInfoLegend = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${toRem(16)};
  align-items: center;
`;

export const ApiarySchemeInfoLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${toRem(12)};
`;

export const ApiarySchemeInfoLegendIcon = styled.div`
  width: ${toRem(40)};
  height: ${toRem(40)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const ApiarySchemeInfoLegendText = styled.span`
  font-size: ${toRem(14)};
  line-height: ${20 / 14};
  color: ${COLORS.FG.DEFAULT};
`;

export const ApiarySchemeInfoHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${toRem(16)};
`;

export const ApiarySchemeInfoCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${toRem(24)};
  height: ${toRem(24)};
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;
  }

  &:hover {
    opacity: 0.7;
  }
`;
export const ApiarySchemeEmptyState = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${toRem(16)};
  text-align: center;
  z-index: 1;
`;

export const ApiarySchemeEmptyTitle = styled.h2`
  font-weight: 700;
  font-size: ${toRem(24)};
  line-height: ${32 / 24};
  color: ${COLORS.FG.DEFAULT};
  margin: 0;
`;

export const ApiarySchemeEmptyDescription = styled.p`
  font-size: ${toRem(14)};
  line-height: ${20 / 14};
  color: ${COLORS.FG.SOFT};
  margin: 0;
  max-width: ${toRem(400)};
`;

export const ApiarySchemeEmptyControls = styled.div`
  position: absolute;
  top: 50%;
  right: ${toRem(20)};
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: ${toRem(1)};
  background-color: ${COLORS.BG.SURFACE1};
  box-shadow:
    0px 12px 20px 0px #4453710d,
    0px 0px 20px 0px #4453711a;
  color: ${COLORS.ACCENT.DEFAULT};
  border-radius: ${toRem(8)};
  overflow: hidden;
  z-index: 1;
`;
